/**
 * WhiteboardContainer - Agora Interactive Whiteboard Component
 *
 * A draggable, resizable whiteboard overlay for meetings
 * Uses white-web-sdk loaded from CDN to avoid mathjs/Fraction.js bundling issues
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Maximize2, Minimize2, Move, Loader2 } from 'lucide-react';
import WhiteboardToolbar from './WhiteboardToolbar';

export default function WhiteboardContainer({
  fastboard, // This is actually { room, sdk } from the CDN-based hook
  isLoading,
  error,
  onClose,
  isHost = false,
  isMobile = false,
}) {
  // Position and size state
  const [position, setPosition] = useState(() => {
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    const width = Math.min(800, windowWidth - 40);
    const height = Math.min(600, windowHeight - 100);
    return {
      x: (windowWidth - width) / 2,
      y: (windowHeight - height) / 2,
    };
  });

  const [size, setSize] = useState(() => ({
    width: Math.min(800, window.innerWidth - 40),
    height: Math.min(600, window.innerHeight - 100),
  }));

  const [isMaximized, setIsMaximized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeHandle, setResizeHandle] = useState(null);
  const [savedState, setSavedState] = useState(null);

  const containerRef = useRef(null);
  const whiteboardRef = useRef(null);
  // Feedback-loop guard for the magix-event camera sync below.
  const applyingRemoteCameraRef = useRef(false);

  // Bind whiteboard room to DOM element when available
  useEffect(() => {
    if (fastboard?.room && whiteboardRef.current) {
      console.log('[WhiteboardContainer] Binding room to element');
      fastboard.room.bindHtmlElement(whiteboardRef.current);

      // Start every user as 'follower'. The active-panner becomes the
      // broadcaster dynamically via the Hand tool in WhiteboardToolbar —
      // that way any writer (moderator / co-host / presenter) panning is
      // propagated to everyone else, not just moderator → others.
      if (fastboard.room.setViewMode) {
        fastboard.room.setViewMode('follower');
      }

      // white-web-sdk 2.16 resets disableSerialization to true whenever the
      // writable state is (re)initialized, which silently drops serialized
      // commands like moveSelected/resize/duplicate. Re-assert it here so the
      // first selector operation after mount actually broadcasts.
      try { fastboard.room.disableSerialization = false; } catch (_) { /* ignore */ }
    }

    return () => {
      // Unbind when unmounting
      if (fastboard?.room?.bindHtmlElement) {
        fastboard.room.bindHtmlElement(null);
      }
    };
  }, [fastboard?.room]);

  // Supplementary bidirectional camera sync via magix events. Netless's built-in
  // broadcaster/follower only supports a single broadcaster at a time — when
  // both host and presenter have the Hand tool active, each is broadcaster
  // locally and neither follows the other, so host can't see presenter's pan
  // (and vice versa). Broadcasting the cameraState via a custom magix event
  // and re-applying it with room.moveCamera gives every user bidirectional
  // pan/zoom sync regardless of viewMode.
  useEffect(() => {
    const room = fastboard?.room;
    if (!room || !room.addMagixEventListener) return;

    const CAMERA_EVENT = 'rlink-camera-sync';

    const onRemoteCamera = (event) => {
      if (event.authorId != null && room.observerId != null && event.authorId === room.observerId) return;
      const p = event.payload || {};
      if (p.centerX == null || p.centerY == null || p.scale == null) return;
      try {
        applyingRemoteCameraRef.current = true;
        room.moveCamera({ centerX: p.centerX, centerY: p.centerY, scale: p.scale, animationMode: 'continuous' });
      } catch (e) {
        console.warn('[WhiteboardContainer] moveCamera failed:', e);
      } finally {
        setTimeout(() => { applyingRemoteCameraRef.current = false; }, 50);
      }
    };

    let pendingCamera = null;
    let rafId = null;
    const flushCamera = () => {
      rafId = null;
      if (!pendingCamera) return;
      const payload = pendingCamera;
      pendingCamera = null;
      try { room.dispatchMagixEvent(CAMERA_EVENT, payload); } catch (_) { /* ignore */ }
    };

    const onStateChanged = (modifyState) => {
      // Writers must always have disableSerialization=false. white-web-sdk
      // 2.16 silently flips it back to true on scene changes / remote ops,
      // which drops serialized commands (moveSelected, resize, undo/redo).
      // Re-assert on every state change for writers — cheap idempotent set.
      if (room.isWritable === true && room.disableSerialization === true) {
        try { room.disableSerialization = false; } catch (_) { /* ignore */ }
      }

      if (!modifyState || !modifyState.cameraState) return;
      if (applyingRemoteCameraRef.current) return;
      // Any writer (host, co-host, presenter) should broadcast their camera
      // state so mousewheel/pinch zoom syncs to everyone — not only when the
      // Hand tool is active. Guests (non-writers) never broadcast.
      if (room.isWritable !== true) return;
      // Avoid feedback loops when Netless is auto-following another writer:
      // if someone else is the active broadcaster, we're receiving their
      // camera state via Netless (not via our magix guard), so re-dispatching
      // would echo back and snap their view. Only broadcast when either WE
      // are the broadcaster or nobody is.
      const bs = room.state?.broadcastState;
      const selfId = room.observerId;
      const otherIsBroadcaster = bs?.broadcasterId != null && bs.broadcasterId !== selfId;
      if (otherIsBroadcaster) return;
      const cs = room.state?.cameraState;
      if (!cs) return;
      pendingCamera = { centerX: cs.centerX, centerY: cs.centerY, scale: cs.scale };
      if (rafId == null) rafId = requestAnimationFrame(flushCamera);
    };

    room.addMagixEventListener(CAMERA_EVENT, onRemoteCamera);
    if (room.callbacks?.on) {
      room.callbacks.on('onRoomStateChanged', onStateChanged);
    }

    return () => {
      if (room.removeMagixEventListener) {
        room.removeMagixEventListener(CAMERA_EVENT, onRemoteCamera);
      }
      if (room.callbacks?.off) {
        room.callbacks.off('onRoomStateChanged', onStateChanged);
      }
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [fastboard?.room]);

  // Sync write permissions when isHost changes (e.g., presenter role added/removed).
  // Only act when isWritable is a real boolean that differs from isHost — right
  // after join it can be undefined, and firing setWritable then would redundantly
  // demote-then-repromote the host, which resets disableSerialization back to
  // the SDK default (true) and silently breaks broadcast of selector operations.
  useEffect(() => {
    if (
      fastboard?.room &&
      typeof fastboard.room.isWritable === 'boolean' &&
      fastboard.room.isWritable !== isHost
    ) {
      console.log('[WhiteboardContainer] Updating writable state:', isHost);
      fastboard.room.setWritable(isHost).then(() => {
        // setWritable resets disableSerialization — re-assert so serialized
        // commands (moveSelected, resize, duplicate, undo/redo) broadcast.
        try { fastboard.room.disableSerialization = false; } catch (_) { /* ignore */ }
      }).catch(err => {
        console.error('[WhiteboardContainer] Failed to update writable state:', err);
      });
    }
  }, [isHost, fastboard?.room]);

  // Drag handlers
  const handleDragStart = useCallback((e) => {
    if (isMaximized || isResizing) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  }, [isMaximized, isResizing]);

  // Resize handlers
  const handleResizeStart = useCallback((e, handle) => {
    if (isMaximized) return;
    e.stopPropagation();
    setResizeHandle(handle);
    setIsResizing(true);
  }, [isMaximized]);

  // Toggle maximize
  const toggleMaximize = useCallback(() => {
    if (isMaximized) {
      // Restore
      if (savedState) {
        setPosition(savedState.position);
        setSize(savedState.size);
      }
      setIsMaximized(false);
    } else {
      // Maximize
      setSavedState({ position, size });
      setPosition({ x: 20, y: 20 });
      setSize({
        width: window.innerWidth - 40,
        height: window.innerHeight - 40,
      });
      setIsMaximized(true);
    }
  }, [isMaximized, position, size, savedState]);

  // Mouse move/up handlers for drag and resize
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const maxX = window.innerWidth - size.width;
        const maxY = window.innerHeight - size.height;
        setPosition({
          x: Math.max(0, Math.min(maxX, e.clientX - dragOffset.x)),
          y: Math.max(0, Math.min(maxY, e.clientY - dragOffset.y)),
        });
      }

      if (isResizing && resizeHandle) {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;

        const minWidth = 400;
        const minHeight = 300;
        const maxWidth = window.innerWidth - rect.left;
        const maxHeight = window.innerHeight - rect.top;

        let newWidth = size.width;
        let newHeight = size.height;

        if (resizeHandle.includes('e')) {
          newWidth = Math.max(minWidth, Math.min(maxWidth, e.clientX - rect.left));
        }
        if (resizeHandle.includes('s')) {
          newHeight = Math.max(minHeight, Math.min(maxHeight, e.clientY - rect.top));
        }
        if (resizeHandle.includes('w')) {
          const diff = rect.left - e.clientX;
          newWidth = Math.max(minWidth, size.width + diff);
          if (newWidth !== size.width) {
            setPosition(prev => ({ ...prev, x: e.clientX }));
          }
        }
        if (resizeHandle.includes('n')) {
          const diff = rect.top - e.clientY;
          newHeight = Math.max(minHeight, size.height + diff);
          if (newHeight !== size.height) {
            setPosition(prev => ({ ...prev, y: e.clientY }));
          }
        }

        setSize({ width: newWidth, height: newHeight });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setResizeHandle(null);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragOffset, resizeHandle, size]);

  // Keep within bounds on window resize
  useEffect(() => {
    const handleResize = () => {
      if (isMaximized) {
        setSize({
          width: window.innerWidth - 40,
          height: window.innerHeight - 40,
        });
      } else {
        setPosition(prev => ({
          x: Math.max(0, Math.min(window.innerWidth - size.width, prev.x)),
          y: Math.max(0, Math.min(window.innerHeight - size.height, prev.y)),
        }));
      }

      // Refresh room view on resize
      if (fastboard?.room?.refreshViewSize) {
        fastboard.room.refreshViewSize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMaximized, size, fastboard]);

  // Refresh view when size changes
  useEffect(() => {
    if (fastboard?.room?.refreshViewSize) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        fastboard.room.refreshViewSize();
      }, 100);
    }
  }, [size, fastboard]);

  // Loading state
  if (isLoading) {
    return (
      <div
        className="fixed z-50 bg-[#1a1f35] rounded-2xl shadow-2xl border border-white/10 flex items-center justify-center"
        style={{
          left: position.x,
          top: position.y,
          width: size.width,
          height: size.height,
        }}
      >
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-[#6a1fbf] animate-spin" />
          <p className="text-gray-400 text-sm">Loading whiteboard...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        className="fixed z-50 bg-[#1a1f35] rounded-2xl shadow-2xl border border-red-500/20 flex flex-col items-center justify-center p-6"
        style={{
          left: position.x,
          top: position.y,
          width: size.width,
          height: size.height,
        }}
      >
        <p className="text-red-400 text-lg mb-2">Failed to load whiteboard</p>
        <p className="text-gray-500 text-sm mb-4">{error.message}</p>
        <button
          onClick={onClose}
          className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
        >
          Close
        </button>
      </div>
    );
  }

  // No room instance
  if (!fastboard?.room) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="fixed z-50 bg-white rounded-xl shadow-2xl overflow-hidden"
      style={{
        left: isMobile ? 16 : position.x,
        top: isMobile ? 16 : position.y,
        width: isMobile ? 'calc(100vw - 32px)' : size.width,
        height: isMobile ? 'calc(100vh - 120px)' : size.height,
        cursor: isDragging ? 'grabbing' : 'default',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
      }}
    >
      {/* Header */}
      <div
        className="h-10 bg-[#030325] flex items-center justify-between px-3 select-none"
        onMouseDown={!isMobile ? handleDragStart : undefined}
        style={{ cursor: !isMobile && !isMaximized ? 'grab' : 'default' }}
      >
        <div className="flex items-center gap-2">
          <Move className="w-4 h-4 text-gray-400" />
          <span className="text-white text-sm font-medium">Whiteboard</span>
        </div>
        <div className="flex items-center gap-1">
          {!isMobile && (
            <button
              onClick={toggleMaximize}
              className="p-1.5 hover:bg-white/10 rounded transition-colors"
              title={isMaximized ? 'Restore' : 'Maximize'}
            >
              {isMaximized ? (
                <Minimize2 className="w-4 h-4 text-gray-400" />
              ) : (
                <Maximize2 className="w-4 h-4 text-gray-400" />
              )}
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-red-500/20 rounded transition-colors"
            title={isHost ? "Close Whiteboard" : "Hide Whiteboard"}
          >
            <X className="w-4 h-4 text-red-400" />
          </button>
        </div>
      </div>

      {/* Whiteboard Content Area */}
      <div className="relative w-full" style={{ height: 'calc(100% - 40px)' }}>
        {/* Toolbar - only shown for hosts */}
        {isHost && <WhiteboardToolbar room={fastboard?.room} isHost={isHost} />}

        {/* Whiteboard Canvas - Native white-web-sdk element binding */}
        <div
          ref={whiteboardRef}
          className="w-full h-full bg-white"
        />
      </div>

      {/* Resize Handles (desktop only, non-maximized) */}
      {!isMobile && !isMaximized && isHost && (
        <>
          {/* Corners */}
          <div
            className="absolute w-3 h-3 bg-blue-500 hover:bg-blue-400 cursor-nwse-resize opacity-60 hover:opacity-100 transition-opacity rounded-full"
            style={{ bottom: -6, right: -6 }}
            onMouseDown={(e) => handleResizeStart(e, 'se')}
          />
          <div
            className="absolute w-3 h-3 bg-blue-500 hover:bg-blue-400 cursor-nesw-resize opacity-60 hover:opacity-100 transition-opacity rounded-full"
            style={{ bottom: -6, left: -6 }}
            onMouseDown={(e) => handleResizeStart(e, 'sw')}
          />
          <div
            className="absolute w-3 h-3 bg-blue-500 hover:bg-blue-400 cursor-nesw-resize opacity-60 hover:opacity-100 transition-opacity rounded-full"
            style={{ top: -6, right: -6 }}
            onMouseDown={(e) => handleResizeStart(e, 'ne')}
          />
          <div
            className="absolute w-3 h-3 bg-blue-500 hover:bg-blue-400 cursor-nwse-resize opacity-60 hover:opacity-100 transition-opacity rounded-full"
            style={{ top: -6, left: -6 }}
            onMouseDown={(e) => handleResizeStart(e, 'nw')}
          />

          {/* Edges */}
          <div
            className="absolute w-full h-1 bg-transparent hover:bg-blue-400/50 cursor-ns-resize"
            style={{ bottom: -2, left: 0 }}
            onMouseDown={(e) => handleResizeStart(e, 's')}
          />
          <div
            className="absolute w-full h-1 bg-transparent hover:bg-blue-400/50 cursor-ns-resize"
            style={{ top: -2, left: 0 }}
            onMouseDown={(e) => handleResizeStart(e, 'n')}
          />
          <div
            className="absolute w-1 h-full bg-transparent hover:bg-blue-400/50 cursor-ew-resize"
            style={{ right: -2, top: 0 }}
            onMouseDown={(e) => handleResizeStart(e, 'e')}
          />
          <div
            className="absolute w-1 h-full bg-transparent hover:bg-blue-400/50 cursor-ew-resize"
            style={{ left: -2, top: 0 }}
            onMouseDown={(e) => handleResizeStart(e, 'w')}
          />
        </>
      )}
    </div>
  );
}
